import React, { useEffect, useMemo, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import axiosInstance from "../utils/axiosInstance";
import PaginationControls from "../components/PaginationControls";
import { formatNumber } from "../utils/numberFormat";
import { Link } from "react-router-dom";
import HotToastMessage from "../components/ui/HotToastMessage";
import Select from "../components/ui/Select";
import { Filter, Plus, Search, X } from "lucide-react";

const ExpensesList = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");

  const [cottageFilter, setCottageFilter] = useState("");
  const [cottageQuery, setCottageQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);

  const [cottageNumbers, setCottageNumbers] = useState([]);
  const [loadingCottages, setLoadingCottages] = useState(false);

  const [toast, setToast] = useState({ message: "", type: "info" });

  const totalPages = count ? Math.ceil(count / pageSize) : 1;
  const hasNext = !!next;
  const hasPrevious = !!previous;

  // ---- React-Select styles (RTL, modern)
  const selectStyles = {
    control: (base) => ({
      ...base,
      background: "rgba(255,255,255,0.98)",
      borderColor: "#E2E8F0",
      borderRadius: "1rem",
      padding: "0.15rem",
      boxShadow: "none",
      minHeight: "44px",
      textAlign: "right",
      ":hover": { borderColor: "#2563EB" },
    }),
    menu: (base) => ({ ...base, textAlign: "right", zIndex: 60 }),
    option: (base, state) => ({
      ...base,
      textAlign: "right",
      backgroundColor: state.isSelected
        ? "#2563EB"
        : state.isFocused
        ? "#DBEAFE"
        : "#fff",
      color: state.isSelected ? "#fff" : "#0F172A",
    }),
    singleValue: (base) => ({ ...base, textAlign: "right" }),
    placeholder: (base) => ({ ...base, textAlign: "right", color: "#64748B" }),
  };

  // ---- Fetch cottage numbers for select
  useEffect(() => {
    const fetchCottageNumbers = async () => {
      setLoadingCottages(true);
      try {
        const response = await axiosInstance.get("/cottages/numbers/");
        if (response.data?.results && Array.isArray(response.data.results)) {
          setCottageNumbers(
            response.data.results
              .map((item) => item.cottage_number)
              .filter((v) => v !== null && v !== undefined && v !== "")
          );
        } else {
          setCottageNumbers([]);
        }
      } catch (err) {
        console.error("خطا در بارگذاری شماره کوتاژها:", err);
        setCottageNumbers([]);
      } finally {
        setLoadingCottages(false);
      }
    };

    fetchCottageNumbers();
  }, []);

  const cottageOptions = useMemo(
    () => [
      { value: "", label: "همه کوتاژها" },
      ...cottageNumbers.map((num) => ({ value: num, label: String(num) })),
    ],
    [cottageNumbers]
  );

  // ---- Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // ---- Debounced filter
  useEffect(() => {
    const handler = setTimeout(() => {
      setCottageQuery(cottageFilter);
      setCurrentPage(1);
    }, 250);
    return () => clearTimeout(handler);
  }, [cottageFilter]);

  // ---- Fetch expenses
  useEffect(() => {
    const fetchExpenses = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = { page: currentPage, page_size: pageSize };
        if (query) params.search = query;
        if (cottageQuery) params.cottage = cottageQuery;

        const { data } = await axiosInstance.get("/expenses/", { params });

        setExpenses(data?.results || []);
        setCount(data?.count || 0);
        setNext(data?.next || null);
        setPrevious(data?.previous || null);
      } catch (err) {
        const message =
          translateApiMessage(err.response?.data?.detail) || "خطایی در بارگذاری هزینه‌ها رخ داد";
        setError(message);
        setToast({ message, type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [currentPage, pageSize, query, cottageQuery]);

  // ---- Pagination handlers
  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((c) => c + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((c) => c - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // ---- Clear filters
  const clearAll = () => {
    setSearchText("");
    setQuery("");
    setCottageFilter("");
    setCottageQuery("");
    setCurrentPage(1);
  };

  const activeFiltersCount = [query, cottageQuery].filter(Boolean).length;

  const chips = useMemo(() => {
    const out = [];
    if (query) out.push({ k: "جستجو", v: query });
    if (cottageQuery) out.push({ k: "کوتاژ", v: cottageQuery });
    return out;
  }, [query, cottageQuery]);

  return (
    <div className="mt-24 pb-24 bg-slate-50 min-h-screen font-vazir" dir="rtl">
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />

      <div className="mx-auto max-w-7xl md:max-w-[90%] px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-white p-5 sm:p-6 shadow-sm ring-1 ring-slate-200">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="text-right">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                لیست هزینه‌ها
              </h2>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                  تعداد: {count ?? 0}
                </span>
                {activeFiltersCount > 0 ? (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700 inline-flex items-center gap-1">
                    <Filter className="w-3.5 h-3.5" />
                    {activeFiltersCount} فیلتر فعال
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                    بدون فیلتر
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAll}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm ring-1 ring-slate-200 hover:bg-slate-50 transition"
                >
                  <X className="w-4 h-4" />
                  پاک‌سازی
                </button>
              )}

              <Link to="/new-expense">
                <button className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition">
                  <Plus className="w-4 h-4" />
                  هزینه جدید
                </button>
              </Link>
            </div>
          </div>

          {/* Filters row */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-slate-700 text-right mb-2">
                جستجو (شرح/شماره کوتاژ):
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="متن جستجو را وارد کنید…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="
                    w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11
                    text-sm text-slate-800 outline-none transition
                    focus:ring-4 focus:ring-blue-100
                  "
                />
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              </div>
            </div>

            {/* Cottage select */}
            <div>
              <label className="block text-sm font-medium text-slate-700 text-right mb-2">
                فیلتر بر اساس شماره کوتاژ:
              </label>
              <Select
                value={
                  cottageOptions.find((opt) => opt.value === cottageFilter) ||
                  null
                }
                onChange={(opt) => setCottageFilter(opt ? opt.value : "")}
                options={cottageOptions}
                isClearable
                isLoading={loadingCottages}
                placeholder={
                  loadingCottages ? "در حال بارگذاری…" : "انتخاب کوتاژ"
                }
                styles={selectStyles}
                className="text-sm"
              />
            </div>
          </div>

          {/* Chips */}
          {chips.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              {chips.map((c, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-100"
                >
                  <b className="font-medium">{c.k}:</b> {c.v}
                </span>
              ))}
            </div>
          )}

          {/* Loading / Error */}
          {loading && (
            <div className="mt-6 grid gap-3">
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
            </div>
          )}

          {error && !loading && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}

          {/* Table & Pagination */}
          {!loading && !error && (
            <>
              {expenses.length === 0 ? (
                <div className="mt-10 text-center text-slate-600">
                  هیچ هزینه‌ای یافت نشد.
                </div>
              ) : (
                <>
                  <div className="mt-5">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[5, 10, 20, 50, 100]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>

                  <div className="mt-3 overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-right text-slate-700">
                            <th className="p-3 font-semibold">ردیف</th>
                            <th className="p-3 font-semibold">شماره کوتاژ</th>
                            <th className="p-3 font-semibold">مبلغ (ریال)</th>
                            <th className="p-3 font-semibold">شرح</th>
                            <th className="p-3 font-semibold">رسید</th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                          {expenses.map((exp, index) => (
                            <tr
                              key={exp.id}
                              className="hover:bg-slate-50 transition"
                            >
                              <td className="p-3 align-middle">
                                {index + 1 + (currentPage - 1) * pageSize}
                              </td>

                              <td className="p-3 align-middle">
                                {exp?.cottage_detail?.cottage_number ? (
                                  <Link
                                    className="text-blue-700 hover:underline"
                                    to={`/cottages/${exp.cottage_detail.cottage_number}`}
                                  >
                                    {exp.cottage_detail.cottage_number}
                                  </Link>
                                ) : (
                                  "—"
                                )}
                              </td>

                              <td className="p-3 align-middle">
                                <bdi dir="ltr" className="tabular-nums">{formatNumber(exp.value, { maximumFractionDigits: 0 })}</bdi>
                              </td>

                              <td className="p-3 align-middle text-slate-700">
                                {exp.description || "—"}
                              </td>

                              <td className="p-3 align-middle">
                                {exp.receipt ? (
                                  <a
                                    href={exp.receipt}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-700 hover:underline"
                                  >
                                    مشاهده رسید
                                  </a>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-3">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[5, 10, 20, 50, 100]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpensesList;
